import { Grid } from 'components/grid/grid';
import SurveyStatisticsModel from 'components/statistics/survey/model';
import CommentsView from 'enums/comments/views';
import { wccModules } from 'enums/wccModules';
import { inject, injectable } from 'inversify';
import { ObservableArray, Subscribable } from 'knockout';
import { ManagersContext } from 'managers/context';
import IdeationManager from 'managers/ideation';
import { IWCCStorageManager } from 'managers/iStorage';
import ItemsPager from 'managers/pager';
import SimpleTopicManager from 'managers/simpleTopic';
import SurveyStatisticsManager from 'managers/statistics/survey';
import CommentsManager from 'managers/topic/comments';
import { EffectsContainer } from 'mixins/withEffects';
import { TopicThread } from 'models/topicThread';
import { CommentVMHeader } from '../comment/comment';
import './comments.html';
import { TopicCommentsVMConfig } from './comments.interfaces';
import './comments.scss';

const { discussionId } = settings;

type CommentsGridItem = TopicThread | SurveyStatisticsModel

components.preload('grid-new', 'topic-comment', 'survey-statistics');

@injectable()
export default class TopicCommentsVM {
    private itemsInViewPort = ko.observableArray<CommentsGridItem>();

    private topicId: Subscribable<string | undefined>
    private view: Subscribable<CommentsView>
    private currentUserId: Subscribable<string | undefined>

    private pager: ItemsPager<TopicThread>

    private allComments: Subscribable<Array<TopicThread>>
    private comments: ObservableArray<TopicThread>
    private newComments: Subscribable<Array<TopicThread>>

    labels = labels;
    
    todoMode: Subscribable<boolean>

    commentHeader: Subscribable<CommentVMHeader | undefined>
    lastComment: Subscribable<TopicThread | undefined>
    newCommentsCountStr: Subscribable<string>

    hasNewComments: Subscribable<boolean>
    showNothingToDoMessage: Subscribable<boolean>
    scrolledToTheBottom: Subscribable<boolean>
    nextPageSpinnerShown: Subscribable<boolean>

    grid: Grid<CommentsGridItem>

    ready: Subscribable<boolean>
    loading: Subscribable<boolean>

    constructor(
        @inject(wccModules.componentConfig) config: TopicCommentsVMConfig,
        @inject(wccModules.componentInfo) info: ko.components.ComponentInfo,
        @inject(wccModules.managersContext) managers: ManagersContext,
        @inject(wccModules.storage) storage: IWCCStorageManager,
        @inject(wccModules.effects) effects: EffectsContainer
    ) {
        this.topicId = ko.flattenComputed(config.topicId);
        this.view = ko.flattenComputed(config.view, CommentsView.all);
        this.todoMode = this.view.is(CommentsView.unread);

        const filters = ko.flattenComputed(config.filters);
        const searchString = filters.pluck(f => f.searchString, '').extend({ rateLimit: { timeout: 500, method: "notifyWhenChangesStop" } });
        const peopleTagsIDs = filters.pluck(f => f.peopleTagsIDs(), []).extend({ rateLimit: { timeout: 500, method: "notifyWhenChangesStop" } });

        const currentUserManager = managers.currentUser(discussionId);
        const currentUser = currentUserManager.pluck(m => m.person);
        this.currentUserId = currentUser.pluck(c => c.personId);

        const topicManager = storage.get(SimpleTopicManager, { topicId: this.topicId });
        const topicLoaded = topicManager.pluck(m => !m.loading(), false);
        const topic = topicManager.pluck(m => m.topic);
        const isIdeation = topic.pluck(t => t.isIdeation(), false);

        const surveyStatManager = storage.get(SurveyStatisticsManager, { topicId: this.topicId });
        const surveyStat = surveyStatManager.pluck(m => m.stat);

        const ideationManager = storage.get(IdeationManager, { topicId: this.topicId });
        const ideationReady = ideationManager.pluck(m => m.isReady, false);
        const isRankingStage = ideationManager.pluck(m => m.isRatingStage, false);
        const isVotingStage = ideationManager.pluck(m => m.isVotingStage, false);

        this.commentHeader = topic.pluck(topic => topic.isIdeation() ? <CommentVMHeader>'title' : undefined);

        const commentsManager = storage.get(CommentsManager, { topicId: this.topicId, view: this.view, searchString, peopleTagsIDs });
        const commentsLoaded = commentsManager.pluck(m => !m.loading(), false);
        const allPagesLoaded = commentsManager.pluck(m => m.allPagesLoaded, false);

        const removedCommentsIDs = ko.observableArray().extend({ deferred: true });
        const doneCommentsIDs = ko.observableArray().extend({ deferred: true });

        this.allComments = commentsManager
            .pluck(m => m.comments, [])
            .filter(comment => !removedCommentsIDs().includes(comment.id() ?? ''))
            .filter(comment => !doneCommentsIDs().includes(comment.id() ?? '') || comment.isInToDoList())
            .sortBy(comment => -(comment.createDate() ?? new Date()))
            .extend({ rateLimit: 0 });

        const pinnedComments = this.allComments.filter(comment => comment.isPinned()).extend({ deferred: true });

        const sortedComments = this.allComments
            .filter(comment => !comment.isPinned())
            .sortBy(comment => {
                if (isRankingStage()) { // in ideation ranking stage
                    if (isVotingStage())
                        return comment.randomIdx(); //during voting we randomly sort ideas to prevent biased votes
                    else
                        return -(comment.vote() ?? 0); //once voting is done we sort ideas by final vote value
                }

                return -comment.createDate().getTime();
            });

        const showShowAllItems = ko.pureComputed(() => this.todoMode() || isIdeation() && isRankingStage());
        
        this.pager = new ItemsPager<TopicThread>({
            items: sortedComments,
            pinnedItems: pinnedComments,
            showAllItems: showShowAllItems
        });

        this.comments = this.pager.items;        
        this.newComments = this.pager.newItems;
        this.lastComment = this.comments.last();

        const pagesCount = this.pager.pagesCount;
        const pagesCountToShow = this.pager.pagesCountToShow;

        const newCommentsCount = this.pager.newItems.mapSingle(items => items.length).extend({ deferred: true });
        this.newCommentsCountStr = newCommentsCount.mapSingle(count => (count === 1 ? labels.LoadNewComment : labels.LoadNewComments).replace('{0}', count.toString()));

        this.hasNewComments = newCommentsCount.is(count => count > 0);
        const noComments = ko.pureComputed(() => this.allComments().length === 0);

        this.showNothingToDoMessage = ko.pureComputed(() => this.todoMode() && noComments());
        this.scrolledToTheBottom = this.lastComment.pluck(comment => this.itemsInViewPort().includes(comment), false);
        const allowToShowNextPage = ko.pureComputed(() => !this.grid.rendering() && this.scrolledToTheBottom());
        const hasPagesToShow = ko.pureComputed(() => pagesCountToShow() < pagesCount());
        this.nextPageSpinnerShown = ko.pureComputed(() => !this.grid.rendering() && (!allPagesLoaded() || hasPagesToShow()));

        const shouldShouldKeepOrder = ko.pureComputed(() => isRankingStage());

        const surveyStatistics = new SurveyStatisticsModel({ TopicId: this.topicId });

        const gridItems = ko.pureComputed(() => {
            const userInstance = currentUser();
            const topicInstance = topic();
            const filtersInstance = filters();

            if (userInstance != undefined && topicInstance != undefined) {
                if (topicInstance.isSurvey()) {
                    const surveyStatInstance = surveyStat();

                    if (surveyStatInstance != undefined) {
                        const surveyStatisticsNeeded = (userInstance.isModerator() || (topicInstance.surveyShowSummaryBox() && this.comments().length > 0)) &&
                            surveyStatInstance.pages().some(p => p.choiceQuestions().length > 0 || p.rankQuestions().length > 0) &&
                            this.view() == CommentsView.all &&
                            this.comments().length > 0 &&
                            (filtersInstance == undefined || !filtersInstance.isActive());

                        if (surveyStatisticsNeeded)
                            return [surveyStatistics, ...this.comments()];
                    }
                }
            }

            return this.comments();
        });

        this.grid = new Grid({
            source: gridItems,
            offset: 5,
            threshold: 100,
            keepOrder: shouldShouldKeepOrder
        });

        this.ready = ko.pureComputed(() => this.currentUserId() != undefined && topicLoaded() && commentsLoaded() && ideationReady());
        this.loading = this.ready.not();

        const shouldAutoSyncComments = ko.pureComputed(() => this.checkIfShouldAutoSyncComments());
        const commentsAreReloaded = ko.pureComputed(() => this.checkIfCommentsReloaded());

        effects.register([
            pinnedComments.subscribe(() => this.pager.afterSyncActions.push(() => this.grid.update(true)))
        ]);

        effects.register(() => {
            if (shouldAutoSyncComments())
                this.pager.sync();
        }, [this.allComments]);

        effects.register((areCommentsReloaded) => {
            if (areCommentsReloaded)
                this.pager.reset();
        }, [commentsAreReloaded]);

        effects.register((allowToShowNextPage, pagesCount) => {
            if (allowToShowNextPage && pagesCountToShow() < pagesCount)
                this.pager.showNextPage();
        }, [allowToShowNextPage, this.pager.pagesCount]);

        effects.register((pagesCountToShow, pagesCount) => {
            if (pagesCountToShow >= pagesCount - 1)
                commentsManager()?.loadMore();
        }, [pagesCountToShow, pagesCount]);

        effects.register((manager, todoMode) => {
            removedCommentsIDs([]);
            doneCommentsIDs([]);

            if (manager != undefined) {
                const currentRemovedComments = manager.comments().filter(comment => comment.isDeleted());
                const currentRemovedCommentsIDs = currentRemovedComments.map(c => c.id() ?? '');

                removedCommentsIDs(currentRemovedCommentsIDs);

                if (todoMode) {
                    const currentDoneComments = manager.comments().filter(comment => !comment.isInToDoList());
                    const currentDoneCommentsIDs = currentDoneComments.map(c => c.id() ?? '');

                    doneCommentsIDs(currentDoneCommentsIDs);
                }
            }
        }, [commentsManager, this.todoMode]);

        ko.applyBindingsToNode(info.element, {
            'css': {
                'topic__comments': true
            }
        });
    }

    syncComments = () => {
        this.pager.sync();
    }

    getItemTemplate = (item: CommentsGridItem) => {
        if (item instanceof TopicThread)
            return 'wcc-components-topic-comments-item-comment';
        else if (item instanceof SurveyStatisticsModel)
            return 'wcc-components-topic-comments-item-surveystat';
    }

    getViewPortTracker = (item: CommentsGridItem) => {
        return ko.pureComputed({
            read: () => this.itemsInViewPort().includes(item),

            write: value => {
                if (value) {
                    if (!this.itemsInViewPort().includes(item))
                        this.itemsInViewPort.push(item);
                } else {
                    this.itemsInViewPort.remove(item);
                }
            }
        });
    }

    private checkIfCommentsReloaded() {
        return this.comments().length === 0 ||
            this.comments().every(comment => !this.allComments().includes(comment));
    }

    private checkIfNewCommentsContainUserComment() {
        return this.newComments().length > 0 &&
            this.currentUserId() != undefined &&
            this.newComments().some(comment => comment.postedById() == this.currentUserId());
    }

    private checkIfAllCommentsBelongToUser() {
        return this.allComments().length > 0 &&
            this.comments().length > 0 &&
            this.currentUserId() != undefined &&
            this.comments().every(comment => comment.postedById() == this.currentUserId());
    }

    private checkIfShouldAutoSyncComments() {
        return this.checkIfCommentsReloaded() || !this.todoMode() && (this.checkIfNewCommentsContainUserComment() || this.checkIfAllCommentsBelongToUser());
    }
}